(ns practitest.frontend.components.project-assistant
  (:require
    [clojure.string :as str]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.common.format-utils :as fu]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components.markdown :refer [markdown-preview]]
    [practitest.frontend.components.project-assistant.actions :as actions]
    [practitest.frontend.components.project-assistant.events :as events]
    [practitest.frontend.components.project-assistant.subs :as project-assistant.subs]
    [practitest.frontend.components.users :as users]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.components2.feedback :as feedback]
    [practitest.frontend.components2.resize-panel :refer [panel-group
                                                          panel-resize-handle
                                                          panel-resizeable]]
    [practitest.frontend.components2.search-box :as search-box]
    [practitest.frontend.components2.spinners :as spinners]
    [practitest.frontend.components2.tabs :refer [tab tabs-container]]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]
    [reagent.core :as reagent]))

;; --- views ---

(defn- assistant-tabs []
  (let [active @(re-frame/subscribe [::project-assistant.subs/active-tab])]
    [tabs-container {:active-tab-id active
                     :on-toggle #(re-frame/dispatch [::events/switch-tab %])}
     [tab {:tab-id :chat :icon [icons/mux-message]}
      (get-translation "assistant.tab.chat" "Chat")]
     [tab {:tab-id :history :icon [icons/mux-clock]}
      (get-translation "assistant.tab.history" "History")]]))

(defn- conversation-row [conv]
  (let [rename-state @(re-frame/subscribe [::project-assistant.subs/rename-state])
        editing? (= (:id rename-state) (:id conv))
        title (:title conv)
        display-title (if (str/blank? title)
                        (get-translation "assistant.new-chat" "New Chat")
                        title)]
    [:div.assistant-conversation-row
     (if editing?
       [:input.assistant-conversation-rename-input
        {:value (:draft rename-state)
         :auto-focus true
         :on-change #(re-frame/dispatch [::events/update-rename-draft (-> % .-target .-value)])
         :on-key-down (fn [e]
                        (case (.-key e)
                          "Enter" (re-frame/dispatch [::events/commit-rename])
                          "Escape" (re-frame/dispatch [::events/cancel-rename])
                          nil))
         :on-blur #(re-frame/dispatch [::events/commit-rename])}]
       [:<>
        [:div.assistant-conversation-row-main
         {:on-click #(re-frame/dispatch [::events/reactivate-conversation (:id conv)])}
         [:div.assistant-conversation-row-title display-title]
         (when-let [timestamp (:last_message_at conv)]
           [:div.assistant-conversation-row-time (fu/format-time-ago timestamp)])]
        [:div.assistant-conversation-row-actions
         [buttons/icon-button
          {:icon [icons/mux-pencil]
           :class "row-action"
           :on-click (fn [e]
                       (.stopPropagation e)
                       (re-frame/dispatch [::events/start-rename (:id conv) title]))}]
         [buttons/icon-button
          {:icon [icons/mux-trash]
           :class "row-action"
           :on-click (fn [e]
                       (.stopPropagation e)
                       (re-frame/dispatch [::events/delete-conversation (:id conv)]))}]]])]))

(defn- history-tab []
  (let [conversations @(re-frame/subscribe [::project-assistant.subs/conversations])
        filtered @(re-frame/subscribe [::project-assistant.subs/filtered-conversations])
        search @(re-frame/subscribe [::project-assistant.subs/history-search])]
    [:div.assistant-history
     [:div.assistant-history-heading
      (get-translation "assistant.history.heading" "Chat History")]
     [search-box/search-textbox
      {:class "assistant-history-search"
       :value search
       :placeholder (get-translation "assistant.history.search" "Search")
       :on-change #(re-frame/dispatch [::events/set-history-search (-> % .-target .-value)])
       :on-clear-click #(re-frame/dispatch [::events/set-history-search ""])}]
     (cond
       (empty? conversations)
       [:div.assistant-empty-history
        (get-translation "assistant.history.empty" "No conversations yet")]

       (empty? filtered)
       [:div.assistant-empty-history
        (get-translation "assistant.history.no-results" "No results")]

       :else
       [:div.assistant-history-list
        (for [conv filtered]
          ^{:key (:id conv)}
          [conversation-row conv])])]))

(defn- message-bubble [{:keys [id role content] :as msg}]
  (case role
    "pending-action"
    [actions/pending-action-bubble msg]

    "tool-call"
    (when-not (str/blank? content)
      [:div.assistant-message.tool-call-message
       [:div.message-text [markdown-preview content]]])

    (when-not (str/blank? content)
      (if (= role "user")
        [:div.assistant-message-row.user-message-row
         [:div.user-message-avatar
          [users/user-avatar2 @(re-frame/subscribe [::subs/current-user])]]
         [:div.assistant-message.user-message
          [:div.message-text content]]]
        [:div.assistant-message.assistant-message-content
         [:div.assistant-message-label
          [:span.assistant-message-label-icon "✨"]
          [:span (get-translation "assistant.title" "Smart Fox Assistant")]]
         [:div.message-text [markdown-preview content {:link-target nil}]]
         (when id
           [feedback/feedback-component
            {:feature "assistant"
             :entity-type :assistant-message
             :entity-id id
             :class "assistant-feedback"
             :mixpanel-up ["Assistant Feedback" {"Rating" "Up"}]
             :mixpanel-down ["Assistant Feedback" {"Rating" "Down"}]}])]))))

(defn- welcome-screen []
  [:div.assistant-welcome
   [:div.welcome-title (get-translation "assistant.welcome.title" "How can I help?")]
   [:div.welcome-suggestions
    [:div.suggestion-chip
     {:on-click #(re-frame/dispatch [::events/send-message "Which tests haven't been run in the last 30 days?"])}
     (get-translation "assistant.welcome.stale-tests" "Tests not run in the last 30 days")]
    [:div.suggestion-chip
     {:on-click #(re-frame/dispatch [::events/send-message "Show requirements without linked tests"])}
     (get-translation "assistant.welcome.unlinked-reqs" "Requirements without linked tests")]
    [:div.suggestion-chip
     {:on-click #(re-frame/dispatch [::events/send-message "Show issues assigned to me"])}
     (get-translation "assistant.welcome.my-issues" "Issues assigned to me")]
    [:div.suggestion-chip
     {:on-click #(re-frame/dispatch [::events/send-message "Show tests linked to open issues"])}
     (get-translation "assistant.welcome.tests-with-issues" "Tests linked to open issues")]]])

;; NOTE: the scroll-to-bottom thing is a little hairy here
;;       not sure it worth the effort, maybe we should just always show "scroll-to-bottom" button?
(defn- messages-list []
  (let [container-ref (atom nil)
        bottom-ref (atom nil)
        programmatic-scroll? (atom false)
        at-bottom? (reagent/atom true)]
    (fn []
      (let [messages @(re-frame/subscribe [::project-assistant.subs/messages])
            streaming? @(re-frame/subscribe [::project-assistant.subs/streaming?])
            error @(re-frame/subscribe [::project-assistant.subs/error])
            retriable? @(re-frame/subscribe [::project-assistant.subs/retriable?])
            at-bottom-p? (fn [el]
                           (< (- (.-scrollHeight el) (.-scrollTop el) (.-clientHeight el)) 50))]
        (when-let [el @container-ref]
          (let [is-at-bottom? (at-bottom-p? el)]
            (when (not= @at-bottom? is-at-bottom?)
              (reset! at-bottom? is-at-bottom?))))
        (when (and @at-bottom? @bottom-ref)
          (reset! programmatic-scroll? true)
          (js/requestAnimationFrame
            (fn []
              (.scrollIntoView @bottom-ref #js {:behavior "smooth"})
              (js/setTimeout #(reset! programmatic-scroll? false) 100))))
        [:<>
         (when (and (empty? messages) (not streaming?))
           [welcome-screen])
         [:div.assistant-messages
          {:ref (fn [el]
                  (reset! container-ref el)
                  (when el
                    (reset! at-bottom? (at-bottom-p? el))))
           :on-scroll (fn []
                        (when-not @programmatic-scroll?
                          (when-let [el @container-ref]
                            (reset! at-bottom? (at-bottom-p? el)))))}
          (for [[idx msg] (map-indexed vector messages)]
            ^{:key idx}
            [message-bubble msg])
          (when error
            [:div.assistant-error
             error
             (when retriable?
               [buttons/button
                {:class "assistant-retry-button"
                 :text (get-translation "generic.button.retry" "Retry")
                 :on-click #(re-frame/dispatch [::events/retry-turn])}])])
          (when-not @at-bottom?
            [:div.assistant-scroll-to-bottom
             {:on-click (fn []
                          (reset! at-bottom? true)
                          (when @bottom-ref
                            (.scrollIntoView @bottom-ref #js {:behavior "smooth"})))}
             [icons/mux-arrow-down]])
          [:div {:ref #(reset! bottom-ref %)
                 :style {:height "1em"}}]]]))))

(defn- input-box []
  (let [input-value (reagent/atom "")
        input-ref (atom nil)]
    (fn []
      (let [streaming? @(re-frame/subscribe [::project-assistant.subs/streaming?])
            connected? @(re-frame/subscribe [::project-assistant.subs/connected?])
            error @(re-frame/subscribe [::project-assistant.subs/error])
            send! (fn []
                    (when (and (not (str/blank? @input-value))
                               (not streaming?)
                               connected?)
                      (re-frame/dispatch [::events/send-message @input-value])
                      (reset! input-value "")
                      (when @input-ref
                        (set! (.-height (.-style @input-ref)) "auto")
                        (.focus @input-ref))))]
        [:<>
         (when (and (not connected?) (not error))
           [:div.assistant-processing-indicator
            [:span (get-translation "assistant.connecting" "Connecting...")]
            [spinners/mux-spinner {:size :small}]])
         (when streaming?
           [:div.assistant-processing-indicator
            [:span (get-translation "assistant.processing" "Processing Result")]
            [spinners/mux-spinner {:size :small}]])
         [:div.assistant-input
          [:textarea.m-input
           {:ref (fn [el]
                   (reset! input-ref el)
                   (when el
                     (dom/focus-on-ref! input-ref false)))
            :value @input-value
            :placeholder (get-translation "assistant.input.placeholder" "Message Smart Fox Assistant...")
            :disabled (not connected?)
            :rows 1
            :on-change (fn [e]
                         (reset! input-value (dom/event-value e))
                         ;; TODO: try `field-sizing: content` (might not work in FF)
                         (let [el (.-target e)]
                           (set! (.-height (.-style el)) "auto")
                           (set! (.-height (.-style el)) (str (.-scrollHeight el) "px"))))
            :on-key-down (fn [e]
                           (when (and (= (.-key e) "Enter")
                                      (not (.-shiftKey e)))
                             (.preventDefault e)
                             (send!)))}]
          [buttons/button
           {:primary? true
            :disabled? (or streaming? (not connected?) (str/blank? @input-value))
            :icon (if streaming?
                    [icons/mux-square]
                    [icons/mux-arrow-right])
            :on-click send!}]]]))))

(defn- panel-header []
  (let [title @(re-frame/subscribe [::project-assistant.subs/conversation-title])]
    [:div.assistant-panel-header
     [:div.assistant-title-area
      [:div.assistant-title-row
       [:span.assistant-title-icon "✨"]
       [:span.assistant-title (get-translation "assistant.title" "Smart Fox Assistant")]
       [:span.assistant-beta-badge (get-translation "assistant.beta" "Beta")]]
      [:div.assistant-conversation-title
       (if (str/blank? title)
         (get-translation "assistant.new-chat" "New Chat")
         title)]]
     [:div.assistant-header-actions
      [buttons/button
       {:class "assistant-new-button"
        :icon [icons/mux-plus-circle]
        :text (get-translation "assistant.new-conversation" "New")
        :on-click #(re-frame/dispatch [::events/new-conversation])}]
      [buttons/icon-button
       {:icon [icons/x-circle]
        :on-click #(re-frame/dispatch [::events/close-panel])}]]]))

(defn assistant-panel []
  (let [budget-warning? @(re-frame/subscribe [::project-assistant.subs/budget-warning?])
        active-tab @(re-frame/subscribe [::project-assistant.subs/active-tab])
        streaming? @(re-frame/subscribe [::project-assistant.subs/streaming?])]
    [:div.assistant-panel
     {:class (when streaming? "streaming")}
     [panel-header]
     [assistant-tabs]
     (if (= active-tab :history)
       [history-tab]
       [:<>
        [messages-list]
        (when budget-warning?
          [:div.assistant-budget-warning
           (get-translation "assistant.budget.warning" "Approaching monthly usage limit")])
        [input-box]])]))

(defn assistant-available? []
  (and @(re-frame/subscribe [::subs/generative-ai-consent?])
       @(re-frame/subscribe [::subs/project-assistant-enabled?])))


(defn body-with-assistant [_body]
  (let [panel-ref (atom nil)
        sync-panel! (fn []
                      (when-let [p @panel-ref]
                        (if @(re-frame/subscribe [::project-assistant.subs/panel-open?])
                          (.expand p)
                          (.collapse p))))]
    (reagent/create-class
      {:display-name "body-with-assistant"
       :component-did-mount (fn [_] (sync-panel!))
       :component-did-update (fn [_] (sync-panel!))
       :reagent-render
       (fn [body]
         (let [panel-open? @(re-frame/subscribe [::project-assistant.subs/panel-open?])]
           [:div.assistant-resizable-container
            [panel-group {:auto-save-id "body-and-assistant"}
             [panel-resizeable {:default-size 70 :min-size 40 :class "main-body-panel"}
              [:div.main-body2 body]]
             [panel-resize-handle
              {:class (when-not panel-open? "resize-handle-hidden")}]
             [panel-resizeable {:default-size 30
                                :min-size 30
                                :collapsible true
                                :collapsed-size 0
                                :panel-ref #(reset! panel-ref %)}
              [assistant-panel]]]]))})))

(comment
  (re-frame/dispatch [::events/dev-add-pending-action
                      "create_custom_view"
                      {:entity_type "Issue"
                       :name "My critical bugs"
                       :view_ptql "Priority = \"High\""
                       :parent_id 21621
                       :parent_path "Issues / Open"}]))
